import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, TrendingUp, TrendingDown, Minus, Download } from "lucide-react";
import { getHome, startRescan, planUrl, fetchReportHtml, reportDownloadUrl } from "@/lib/pay";
import { fetchMe, cachedHome, setCachedHome } from "@/lib/me";
import { SubNav } from "@/components/revanche/SubNav";
import { ReportFrame } from "@/components/revanche/ReportFrame";

const SUB_LABELS = { symetrie: "Symétrie", machoire: "Mâchoire", regard: "Regard", peau: "Peau", carrure: "Carrure", style: "Style", posture: "Posture",
    visage: "Visage", physique: "Physique", cheveux_barbe: "Cheveux & barbe" };
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" }) : null);

const Delta = ({ now, before }) => {
    if (now == null || before == null) return null;
    const d = now - before;
    const Icon = d > 0 ? TrendingUp : d < 0 ? TrendingDown : Minus;
    const color = d > 0 ? "text-revred" : d < 0 ? "text-revtext-secondary" : "text-revtext-secondary";
    return (
        <span className={`inline-flex items-center gap-1 text-xs font-semibold ${color}`}>
            <Icon size={13} />{d > 0 ? `+${d}` : d}
        </span>
    );
};

const Accueil = () => {
    const navigate = useNavigate();
    const [home, setHome] = useState(cachedHome());
    const [busy, setBusy] = useState(false);
    const [reportHtml, setReportHtml] = useState("");

    useEffect(() => {
        // L'accueil complet est réservé aux abonnés confirmés. Sinon → aperçu verrouillé.
        fetchMe().then((me) => {
            if (me && me.questionnaire_termine && !me.abonne) { navigate("/apercu", { replace: true }); return; }
            getHome().then((d) => { setHome(d); setCachedHome(d); }).catch(() => setHome((h) => h || false));
            // Rapport complet déflouté, lisible en entier + téléchargeable (persiste après réouverture).
            fetchReportHtml().then(setReportHtml).catch(() => {});
        }).catch(() => getHome().then((d) => { setHome(d); setCachedHome(d); }).catch(() => setHome((h) => h || false)));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const rescan = async () => {
        if (busy) return;
        setBusy(true);
        try {
            const d = await startRescan();
            localStorage.setItem("revanche_session_id", d.session_id);
            localStorage.setItem("revanche_answers", JSON.stringify(d.answers || {}));
            localStorage.setItem("revanche_step", "0");
            localStorage.setItem("revanche_rescan", "1");
            localStorage.removeItem("revanche_analysis");
            navigate("/analyse");
        } catch (e) { setBusy(false); }
    };

    if (home === false) {
        return (
            <div className="fixed inset-0 bg-black max-w-[480px] mx-auto flex flex-col" data-testid="accueil-screen">
                <div className="flex-1 flex items-center justify-center px-6 text-center">
                    <p className="text-white">Termine ton analyse pour accéder à ton espace.</p>
                </div>
                <SubNav />
            </div>
        );
    }
    if (!home) return <div className="fixed inset-0 bg-black" />;

    const latest = home.latest;
    const prev = home.previous;
    const subKeys = latest ? Object.keys(latest.sous_scores || {}) : [];

    return (
        <div className="fixed inset-0 bg-black max-w-[480px] mx-auto flex flex-col" data-testid="accueil-screen">
            <div className="flex-1 min-h-0 overflow-y-auto scrollbar-none px-5 pt-6 pb-4 safe-top">
                <h1 className="font-display font-black uppercase text-white text-3xl">
                    {home.prenom ? `Salut ${home.prenom}` : "Ton espace"}
                </h1>

                {home.rescan_due && (
                    <div data-testid="rescan-banner" className="mt-4 rounded-xl border border-revred bg-revred/10 p-4">
                        <p className="text-white font-semibold">Ça fait un mois.</p>
                        <p className="text-sm text-white/85 mt-1">Refais ton scan et vois ta progression.</p>
                        <button data-testid="banner-rescan-btn" onClick={rescan} disabled={busy}
                            className="tap-feedback mt-3 h-11 px-5 rounded-xl bg-revred text-white font-display font-black uppercase text-sm disabled:opacity-50">
                            Refaire mon scan
                        </button>
                    </div>
                )}

                {latest ? (
                    <div className="mt-5 bg-ink-900 border border-ink-700 rounded-xl p-5">
                        <p className="text-sm text-white/80">Dernier score d'attirance</p>
                        <div className="flex items-end gap-3">
                            <span data-testid="latest-score" className="font-display font-black text-revred leading-none" style={{ fontSize: "clamp(56px, 20vw, 84px)" }}>
                                {latest.score}%
                            </span>
                            {prev && <span className="mb-3"><Delta now={latest.score} before={prev.score} /></span>}
                        </div>
                        {latest.type?.nom && (
                            <p className="font-display font-black text-white text-xl uppercase mt-1">{latest.type.nom}</p>
                        )}
                        <div className="grid grid-cols-3 gap-2 mt-4">
                            {subKeys.map((k) => (
                                <div key={k} className="rounded-lg border border-ink-700 p-2 text-center">
                                    <div className="font-display font-black text-white text-2xl leading-none">
                                        {latest.sous_scores[k] ?? "—"}
                                    </div>
                                    <div className="text-[10px] text-revtext-secondary mt-1">{SUB_LABELS[k] || k}</div>
                                    {prev && <div className="mt-0.5"><Delta now={latest.sous_scores[k]} before={prev.sous_scores?.[k]} /></div>}
                                </div>
                            ))}
                        </div>
                    </div>
                ) : (
                    <p className="mt-5 text-white/80">Aucune analyse pour l'instant.</p>
                )}

                {!home.rescan_due && (
                    <button data-testid="rescan-btn" onClick={rescan} disabled={busy}
                        className="tap-feedback mt-4 w-full h-13 min-h-[52px] rounded-xl border border-ink-700 text-white font-display font-black uppercase disabled:opacity-50">
                        Refaire mon scan
                    </button>
                )}

                {reportHtml && (
                    <div className="mt-6" data-testid="accueil-report">
                        <div className="flex items-center justify-between mb-3">
                            <p className="font-display font-black text-white text-lg uppercase">Ton analyse complète</p>
                            <a href={reportDownloadUrl()} download data-testid="accueil-download-report"
                                className="tap-feedback flex items-center gap-1.5 font-body rounded-full"
                                style={{ fontSize: 12, color: "var(--text-secondary)", border: "1px solid var(--border)", padding: "0 12px", height: 32 }}>
                                <Download size={14} strokeWidth={1.5} /> Télécharger
                            </a>
                        </div>
                        <div className="relative rounded-xl overflow-hidden" style={{ height: 420, border: "1px solid var(--border)" }}>
                            <ReportFrame html={reportHtml} locked={false} />
                        </div>
                    </div>
                )}

                {prev && (
                    <div className="mt-6" />
                )}

                <div className="mt-6">
                    <p className="font-display font-black text-white text-lg uppercase mb-3">Mes plans</p>
                    {home.plans?.length ? (
                        <div className="flex flex-col gap-2">
                            {home.plans.map((p) => (
                                <div key={p.plan_id} data-testid={`plan-${p.plan_id}`}
                                    className="flex items-center gap-3 bg-ink-900 border border-ink-700 rounded-xl p-3">
                                    <a href={planUrl(p.plan_id)} target="_blank" rel="noreferrer" data-testid={`plan-open-${p.plan_id}`}
                                        className="tap-feedback flex items-center gap-3 min-w-0 flex-1">
                                        <FileText size={20} className="text-revred shrink-0" />
                                        <div className="min-w-0">
                                            <p className="text-white text-sm font-semibold truncate">{p.titre}</p>
                                            <p className="text-[11px] text-revtext-secondary">Créé le {fmtDate(p.created_at)}</p>
                                            <p className="text-[11px] text-revtext-secondary">
                                                {p.last_viewed_at ? `Consulté le ${fmtDate(p.last_viewed_at)}` : "Jamais consulté"}
                                                {p.last_downloaded_at ? ` · Téléchargé le ${fmtDate(p.last_downloaded_at)}` : ""}
                                            </p>
                                        </div>
                                    </a>
                                    <a href={planUrl(p.plan_id, true)} download data-testid={`plan-download-${p.plan_id}`}
                                        className="tap-feedback shrink-0 w-10 h-10 rounded-full border border-ink-700 flex items-center justify-center">
                                        <Download size={16} className="text-white" />
                                    </a>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-sm text-revtext-secondary">Aucun plan encore. Demande à Mike de t'en préparer un.</p>
                    )}
                </div>

                {home.history?.length > 1 && (
                    <div className="mt-6">
                        <p className="font-display font-black text-white text-lg uppercase mb-3">Mes analyses</p>
                        <div className="flex flex-col gap-2">
                            {home.history.map((a, i) => (
                                <div key={i} className="flex items-center justify-between bg-ink-900 border border-ink-700 rounded-xl px-4 py-3">
                                    <span className="text-sm text-white/85">{a.created_at ? new Date(a.created_at).toLocaleDateString("fr-FR") : ""}</span>
                                    <span className="font-display font-black text-white">{a.score}%</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
            <SubNav />
        </div>
    );
};

export default Accueil;
